import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Search, BadgePercent } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Spinner } from "./common";
import { api, errMsg, fmtINR, fmtDate } from "../lib/api";
import { ReadOnlyNotice } from "./FeatureGate";

const DEMAND_TYPE_LABEL = { property_tax: "Property Tax", user_charge: "User Charges", both: "Both" };
const RULE_TYPE_LABEL = { discount: "Discount", penalty: "Penalty" };

const emptyForm = {
  rule_type: "discount", demand_type: "both", scope: "all", pin: "", property_id: "",
  period_type: "date_range", date_from: "", date_to: "", mode: "percent", value: "",
};

export const PenaltyDiscountRules = ({ code, readOnly = false }) => {
  const [rules, setRules] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [prop, setProp] = useState(null);
  const [looking, setLooking] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/t/${code}/rate-engine/penalty-discount-rules`)
      .then((r) => setRules(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [code]);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const lookupPin = async () => {
    if (!form.pin.trim()) return toast.error("Enter a PIN to search");
    setLooking(true);
    try {
      const r = await api.get(`/t/${code}/properties`, { params: { search: form.pin.trim(), limit: 5 } });
      const match = (r.data.items || r.data || []).find?.((p) => p.pin === form.pin.trim())
        || (r.data.items || r.data || [])[0];
      if (!match) { setProp(null); return toast.error("No property found for this PIN"); }
      setProp(match);
      setForm((f) => ({ ...f, property_id: match.id, pin: match.pin }));
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLooking(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/t/${code}/rate-engine/penalty-discount-rules`, {
        ...form, value: Number(form.value) || 0,
        date_from: form.date_from || null, date_to: form.date_to || null,
        property_id: form.scope === "property" ? form.property_id : null,
        pin: form.scope === "property" ? form.pin : null,
      });
      toast.success("Rule created");
      setOpen(false); setForm(emptyForm); setProp(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const toggle = async (r) => {
    try {
      const res = await api.put(`/t/${code}/rate-engine/penalty-discount-rules/${r.id}/toggle`);
      setRules((list) => list.map((x) => x.id === r.id ? { ...x, is_active: res.data.is_active } : x));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (id) => {
    try {
      await api.delete(`/t/${code}/rate-engine/penalty-discount-rules/${id}`);
      toast.success("Rule deleted");
      setRules((list) => list.filter((x) => x.id !== id));
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!rules) return <Spinner label="Loading rules..." />;

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <BadgePercent className="h-4 w-4" /> Penalty & Discount rules
            </CardTitle>
            <CardDescription>
              Applied automatically when payment is collected. Calculated on the demand generation amount.
              Discount applies only when a demand is settled in full; penalty is added once and tracked in the pending balance.
            </CardDescription>
          </div>
          {readOnly ? <ReadOnlyNotice what="penalty & discount rules" testId="pd-readonly-notice" /> : <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="pd-add-rule-button"><Plus className="h-3.5 w-3.5 mr-1.5" /> Add rule</Button>
            </DialogTrigger>
            <DialogContent className="max-w-md" data-testid="pd-rule-dialog">
              <DialogHeader><DialogTitle className="font-heading">New penalty / discount rule</DialogTitle></DialogHeader>
              <form onSubmit={save} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Rule type</Label>
                    <Select value={form.rule_type} onValueChange={(v) => v && set("rule_type")(v)}>
                      <SelectTrigger data-testid="pd-rule-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="discount" data-testid="pd-type-discount">Discount</SelectItem>
                        <SelectItem value="penalty" data-testid="pd-type-penalty">Penalty</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Applies to</Label>
                    <Select value={form.demand_type} onValueChange={(v) => v && set("demand_type")(v)}>
                      <SelectTrigger data-testid="pd-demand-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="property_tax">Property Tax</SelectItem>
                        <SelectItem value="user_charge">User Charges</SelectItem>
                        <SelectItem value="both">Both</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-1">
                  <Label>Scope</Label>
                  <RadioGroup value={form.scope} onValueChange={(v) => { set("scope")(v); if (v === "all") set("period_type")("date_range"); }} className="flex gap-4">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="all" id="pd-scope-all" data-testid="pd-scope-all" />
                      <Label htmlFor="pd-scope-all" className="font-normal cursor-pointer">All properties</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="property" id="pd-scope-property" data-testid="pd-scope-property" />
                      <Label htmlFor="pd-scope-property" className="font-normal cursor-pointer">Specific property</Label>
                    </div>
                  </RadioGroup>
                </div>

                {form.scope === "property" && (
                  <div className="space-y-1">
                    <Label>Property PIN</Label>
                    <div className="flex gap-2">
                      <Input value={form.pin} onChange={(e) => { set("pin")(e.target.value); setProp(null); set("property_id")(""); }}
                        placeholder="Enter PIN" data-testid="pd-pin-input" />
                      <Button type="button" variant="outline" onClick={lookupPin} disabled={looking} data-testid="pd-pin-lookup-button">
                        <Search className="h-4 w-4" />
                      </Button>
                    </div>
                    {prop && (
                      <p className="text-xs text-muted-foreground" data-testid="pd-pin-match">
                        Matched: <span className="font-medium text-foreground">{prop.pin}</span> — {prop.owner_name}
                      </p>
                    )}
                  </div>
                )}

                <div className="space-y-1">
                  <Label>Applicable period</Label>
                  <RadioGroup value={form.period_type} onValueChange={(v) => v && set("period_type")(v)} className="flex gap-4">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="date_range" id="pd-period-range" data-testid="pd-period-range" />
                      <Label htmlFor="pd-period-range" className="font-normal cursor-pointer">Date range</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="one_time" id="pd-period-onetime" disabled={form.scope !== "property"} data-testid="pd-period-onetime" />
                      <Label htmlFor="pd-period-onetime" className={`font-normal cursor-pointer ${form.scope !== "property" ? "opacity-50" : ""}`}>
                        One-time (specific property)
                      </Label>
                    </div>
                  </RadioGroup>
                  {form.period_type === "one_time" && (
                    <p className="text-[11px] text-muted-foreground">
                      No date limit — stays active on this property until you deactivate the rule.
                    </p>
                  )}
                </div>

                {form.period_type === "date_range" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label>From</Label>
                      <Input type="date" value={form.date_from} onChange={(e) => set("date_from")(e.target.value)} required data-testid="pd-date-from" />
                    </div>
                    <div className="space-y-1">
                      <Label>To</Label>
                      <Input type="date" value={form.date_to} onChange={(e) => set("date_to")(e.target.value)} required data-testid="pd-date-to" />
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Mode</Label>
                    <Select value={form.mode} onValueChange={(v) => v && set("mode")(v)}>
                      <SelectTrigger data-testid="pd-mode-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percent">Percentage (%)</SelectItem>
                        <SelectItem value="flat">Flat amount (₹)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>{form.mode === "percent" ? "Percent value" : "Amount (₹)"}</Label>
                    <Input type="number" step="0.01" min="0.01" max={form.mode === "percent" ? 100 : undefined}
                      value={form.value} onChange={(e) => set("value")(e.target.value)} required data-testid="pd-value-input" />
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={saving} data-testid="pd-save-rule-button">
                  {saving ? "Saving..." : "Create rule"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>}
        </div>
      </CardHeader>
      <CardContent>
        {rules.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground" data-testid="pd-empty-state">
            No penalty or discount rules yet. Payments are collected at plain demand value (penalty 0, discount 0).
          </div>
        ) : (
          <Table data-testid="pd-rules-table">
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Applies to</TableHead>
                <TableHead>Scope</TableHead>
                <TableHead className="text-right">Value</TableHead>
                <TableHead>Period</TableHead>
                <TableHead className="text-right">Active</TableHead>
                {!readOnly && <TableHead className="w-10"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rules.map((r) => (
                <TableRow key={r.id} data-testid={`pd-rule-row-${r.id}`}>
                  <TableCell>
                    <Badge variant="outline" className={r.rule_type === "discount"
                      ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                      : "border-amber-300 bg-amber-50 text-amber-900"}>
                      {RULE_TYPE_LABEL[r.rule_type]}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">{DEMAND_TYPE_LABEL[r.demand_type] || r.demand_type}</TableCell>
                  <TableCell className="text-sm">
                    {r.scope === "property" ? <span className="font-mono text-xs">{r.pin}</span> : "All properties"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    {r.mode === "percent" ? `${r.value}%` : fmtINR(r.value)}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {r.period_type === "one_time" ? "One-time (until deactivated)" : `${fmtDate(r.date_from)} → ${fmtDate(r.date_to)}`}
                  </TableCell>
                  <TableCell className="text-right">
                    <Switch checked={!!r.is_active} onCheckedChange={() => toggle(r)} disabled={readOnly} data-testid={`pd-toggle-${r.id}`} />
                  </TableCell>
                  {!readOnly && <TableCell>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" onClick={() => del(r.id)}
                      data-testid={`pd-delete-${r.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
